import { useEffect } from 'react';

interface SEOProps {
    title: string;
    description: string;
    canonical: string;
    ogImage?: string;
    ogType?: string;
    noIndex?: boolean;
}

const DEFAULT_OG_IMAGE =
    'https://rafflekit.app/og-image.png';

export function SEO({
    title,
    description,
    canonical,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    noIndex = false,
}: SEOProps) {
    useEffect(() => {
        // Title
        document.title = title;

        // Helper to set meta tag
        const setMeta = (
            selector: string,
            attr: string,
            content: string
        ) => {
            let el = document.querySelector(
                selector
            ) as HTMLMetaElement;
            if (!el) {
                el = document.createElement('meta');
                const [attrName, attrVal] =
                    attr.split('=');
                el.setAttribute(
                    attrName,
                    attrVal.replace(/"/g, '')
                );
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        // Helper to set link tag
        const setLink = (
            rel: string,
            href: string
        ) => {
            let el = document.querySelector(
                `link[rel="${rel}"]`
            ) as HTMLLinkElement;
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        // Standard meta
        setMeta(
            'meta[name="description"]',
            'name="description"',
            description
        );

        // Robots
        setMeta(
            'meta[name="robots"]',
            'name="robots"',
            noIndex
                ? 'noindex, nofollow'
                : 'index, follow'
        );

        // Canonical
        setLink('canonical', canonical);

        // OG tags
        setMeta(
            'meta[property="og:title"]',
            'property="og:title"',
            title
        );
        setMeta(
            'meta[property="og:description"]',
            'property="og:description"',
            description
        );
        setMeta(
            'meta[property="og:url"]',
            'property="og:url"',
            canonical
        );
        setMeta(
            'meta[property="og:image"]',
            'property="og:image"',
            ogImage
        );
        setMeta(
            'meta[property="og:type"]',
            'property="og:type"',
            ogType
        );

        // Twitter tags
        setMeta(
            'meta[name="twitter:title"]',
            'name="twitter:title"',
            title
        );
        setMeta(
            'meta[name="twitter:description"]',
            'name="twitter:description"',
            description
        );
        setMeta(
            'meta[name="twitter:image"]',
            'name="twitter:image"',
            ogImage
        );

    }, [
        title, description, canonical,
        ogImage, ogType, noIndex
    ]);

    return null;
}
